
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import Index from "./pages/Index";
import Login from "./pages/Login";
import SignUp from "./pages/SignUp";
import ForgotPassword from "./pages/ForgotPassword";
import Dashboard from "./pages/Dashboard";
import FlaggedMessages from "./pages/FlaggedMessages";
import NotFound from "./pages/NotFound";
import Terms from "./pages/Terms";
import Privacy from "./pages/Privacy";
import About from "./pages/About";
import { AuthProvider, useAuth } from "./hooks/useAuth";
import { AnalyticsProvider } from "./lib/analytics";
import { useEffect, useState } from "react";
import { checkApiHealth } from "./lib/api";
import { PwaInstallBanner } from "./components/PwaInstallBanner";
import { setDoc, doc } from "firebase/firestore";
import { db } from "./lib/firebase";
import { useTranslation } from "react-i18next";
import ErrorBoundary from "./components/ErrorBoundary";
import { useSentryErrorCapture } from "./hooks/useSentryErrorCapture";
import { measureAsyncOperation, LoadingPhase } from "./hooks/useLoadingMetrics";
import { NotificationPreferencesProvider } from "./context/NotificationPreferencesContext";
import { IncidentProvider } from "./context/IncidentContext";
import IncidentDashboard from "./pages/IncidentDashboard";
import ShortLinkResolver from "./pages/ShortLinkResolver";



const queryClient = new QueryClient();

// Protected route component
const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="h-screen w-full flex items-center justify-center">
        <div className="animate-pulse-subtle">
          <div className="h-16 w-16 rounded-full mishu-gradient"></div>
        </div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  return <>{children}</>;
};

// Redirect authenticated users from landing page to dashboard
const AuthAwareIndex = ({ apiHealthy }: { apiHealthy: boolean | null }) => {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) return <Navigate to="/dashboard" replace />;
  return <Index apiHealthy={apiHealthy} />;
};

// Redirect component with params
const RedirectToNewFormat = () => {
  const { connectionId } = useParams();
  return <Navigate to={`/${connectionId}/conversations`} replace />;
};

// App component wrapped with BrowserRouter
const AppWithAuth = () => {
  const [apiHealthy, setApiHealthy] = useState<boolean | null>(null);
  const { user } = useAuth();
  
  // Initialize Sentry error capture
  useSentryErrorCapture();

  useEffect(() => {
    const checkHealth = async () => {
      try {
        const result = await measureAsyncOperation(
          LoadingPhase.API_HEALTH_CHECK,
          async () => {
            const isHealthy = await checkApiHealth();
            return { isHealthy, timestamp: Date.now() };
          },
          {
            endpoint: '/health',
            timeout: 5000
          }
        );
        
        setApiHealthy(result.isHealthy);
      } catch (error) {
        console.error('API health check failed:', error);
        setApiHealthy(false);
      }
    };
    
    checkHealth();
  }, []);
  

  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <AuthProvider>
            <NotificationPreferencesProvider>
              <IncidentProvider>
              <LocaleManager />
              <AnalyticsProvider>
                <Toaster />
                <Sonner />
                <PwaInstallBanner />

            <Routes>
              <Route path="/" element={<AuthAwareIndex apiHealthy={apiHealthy} />} />
              <Route path="/login" element={<Login />} />
              <Route path="/signup" element={<SignUp />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/about" element={<About />} />
              <Route 
                path="/dashboard" 
                element={
                  <ProtectedRoute>
                    <Dashboard />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/connections" 
                element={
                  <ProtectedRoute>
                    <Dashboard showConnections={true} />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/settings" 
                element={
                  <ProtectedRoute>
                    <Dashboard showSettings={true} />
                  </ProtectedRoute>
                } 
              />
              {/* Short link resolver for notification deep links */}
              <Route path="/i/:token" element={<ShortLinkResolver />} />
              {/* ADC-006A v2.0.0: Incident-first routes */}
              <Route
                path="/incidents"
                element={
                  <ProtectedRoute>
                    <IncidentDashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/incidents/:connectionId/:conversationId/:incidentId"
                element={
                  <ProtectedRoute>
                    <IncidentDashboard />
                  </ProtectedRoute>
                }
              />
              {/* Conversation routes */}
              <Route 
                path="/:connectionId/conversations" 
                element={
                  <ProtectedRoute>
                    <FlaggedMessages />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/:connectionId/conversations/:conversationId" 
                element={
                  <ProtectedRoute>
                    <FlaggedMessages />
                  </ProtectedRoute>
                } 
              />
              {/* Redirect old route to new format */}
              <Route 
                path="/flagged-messages/:connectionId" 
                element={<RedirectToNewFormat />} 
              />
              <Route path="*" element={<NotFound />} />
            </Routes>
            </AnalyticsProvider>
              </IncidentProvider>
            </NotificationPreferencesProvider>
          </AuthProvider>
        </TooltipProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
};

const LocaleManager = () => {
  const { user } = useAuth();
  const { i18n } = useTranslation();
  
  useEffect(() => {
    const saveAndLoadLocale = async () => {
      if (!user) return;

      try {
        const path = `users/${user.uid}/settings/locale`;
        const lang = i18n.language.split('-')[0];
        await setDoc(doc(db, path), { lang });
          
      } catch (error) {
        console.error("Error managing locale:", error);
      }
    };

    saveAndLoadLocale();
  }, [user, i18n.language]); 

  // Set document direction for RTL/LTR based on language
  useEffect(() => {
    const lang = i18n.language.split('-')[0];
    const dir = lang === 'he' || lang === 'ar' || lang === 'fa' ? 'rtl' : 'ltr';
    if (document?.documentElement) {
      document.documentElement.setAttribute('dir', dir);
      document.documentElement.setAttribute('lang', lang);
    }
  }, [i18n.language]);
  
  return null; 
};

// Main App component
const App = () => (
  <BrowserRouter>
    <AppWithAuth />
  </BrowserRouter>
);

export default App;
